import React from 'react';
import Select from 'react-select';

const SearchableSelect = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select...", 
  isRequired = false, 
  error = null,
  className = "",
  isDisabled = false,
  isSearchable = true,
  isClearable = false,
  name = "",
  label = "",
  ...props 
}) => {
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '48px', // Match the py-3 height
      borderColor: error ? '#ef4444' : state.isFocused ? '#25d366' : '#d1d5db',
      borderWidth: '1px',
      borderRadius: '0.5rem',
      boxShadow: state.isFocused 
        ? error 
          ? '0 0 0 2px rgba(239, 68, 68, 0.2)' 
          : '0 0 0 2px rgba(37, 211, 102, 0.2)'
        : provided.boxShadow,
      '&:hover': {
        borderColor: error ? '#ef4444' : state.isFocused ? '#25d366' : '#9ca3af'
      },
      fontSize: '0.875rem', // text-sm
      backgroundColor: isDisabled ? '#f9fafb' : provided.backgroundColor,
      cursor: isDisabled ? 'not-allowed' : 'default'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#25d366' 
        : state.isFocused 
          ? '#dcfce7' 
          : provided.backgroundColor,
      color: state.isSelected ? 'white' : '#374151',
      fontSize: '0.875rem',
      padding: '8px 12px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: state.isSelected ? '#22c55e' : '#f0fdf4'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
      fontSize: '0.875rem'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#374151',
      fontSize: '0.875rem'
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.5rem',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      border: '1px solid #e5e7eb',
      zIndex: 9999
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '200px',
      borderRadius: '0.5rem'
    }),
    input: (provided) => ({
      ...provided,
      fontSize: '0.875rem',
      color: '#374151'
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: '#d1d5db'
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? '#25d366' : '#6b7280',
      '&:hover': {
        color: '#25d366'
      }
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#6b7280',
      '&:hover': {
        color: '#ef4444'
      }
    })
  };

  const formatOptionLabel = (option, { context }) => {
    if (context === 'menu') {
      return (
        <div className="flex items-center">
          {option.icon && <span className="mr-2">{option.icon}</span>}
          <span>{option.label}</span>
          {option.description && (
            <span className="ml-auto text-xs text-gray-500">{option.description}</span>
          )}
        </div>
      );
    }
    return (
      <div className="flex items-center">
        {option.icon && <span className="mr-2">{option.icon}</span>}
        <span>{option.label}</span>
      </div>
    );
  };

  const handleChange = (selectedOption) => {
    if (onChange) {
      if (name) {
        // Simulate native select onChange event
        const event = {
          target: {
            name: name,
            value: selectedOption ? selectedOption.value : ''
          }
        };
        onChange(event);
      } else {
        onChange(selectedOption);
      }
    }
  };

  const handleChangeWrapper = (selectedOption, actionMeta) => {
    // Handle clear action
    if (actionMeta?.action === 'clear') {
      handleChange(null);
      return;
    }

    // Handle select action
    if (actionMeta?.action === 'select-option' || actionMeta?.action === 'create-option') {
      handleChange(selectedOption);
      return;
    }

    // Handle other actions
    handleChange(selectedOption);
  };

  const selectedOption = options.find(option => option.value === value) || null;

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {isRequired && <span className="text-red-500">*</span>}
        </label>
      )}
      <Select
        options={options}
        value={selectedOption}
        onChange={handleChangeWrapper}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        isClearable={isClearable}
        styles={customStyles}
        formatOptionLabel={formatOptionLabel}
        noOptionsMessage={() => "No options found"}
        loadingMessage={() => "Loading..."}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        {...props}
      />
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default SearchableSelect;
